import ks from "./kBrowserShared";

/** A swipe: -1 = to the previous item (finger moved right), 1 = to the next item (finger moved left). */
export type kSwipeAction = (Direction: number) => void;

/**
 * Horizontal swipe detection on one element (touch events, so a phone's vertical scroll keeps working). A swipe counts
 * when the finger moved at least ThresholdPx sideways and clearly more sideways than up or down (MostlyRatio). A touch
 * that starts in a text field, an editable element or a horizontally scrollable block (code, a wide table) is ignored,
 * so those keep their own gestures. Attach / Detach add and remove the listeners.
 */
export class kSwipeDetector {
    public ThresholdPx: number = 60;
    /** |dx| must be at least this many times |dy|. */
    public MostlyRatio: number = 1.5;
    /** A touch starting this close (px) to the left or right screen edge is ignored, so the phone's own edge-swipe back
     *  gesture (Android) keeps working. 0 = no margin (the default, unchanged behaviour). */
    public EdgeIgnorePx: number = 0;
    private StartX: number = 0;
    private StartY: number = 0;
    private Tracking: boolean = false;

    constructor(private readonly Target: HTMLElement, private readonly OnSwipe: kSwipeAction, ThresholdPx: number = 60) {
        this.ThresholdPx = ThresholdPx;
    }

    public Attach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Target.addEventListener("touchstart", this.OnStart, { passive: true });
            this.Target.addEventListener("touchend", this.OnEnd, { passive: true });
            this.Target.addEventListener("touchcancel", this.OnCancel, { passive: true });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.Attach");
        }
    };

    public Detach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Target.removeEventListener("touchstart", this.OnStart);
            this.Target.removeEventListener("touchend", this.OnEnd);
            this.Target.removeEventListener("touchcancel", this.OnCancel);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.Detach");
        }
    };

    /** true when a touch starting on El must not be read as a swipe: a field, editable text or a sideways scroller. */
    public static Ignored = (El: Element | null, Stop: Element): boolean => {
        if (ks.KErrorMode === true) { return true; }
        try {
            let Cur: Element | null = El;
            while (Cur !== null && Cur !== Stop) {
                const Tag: string = Cur.tagName;
                if (Tag === "INPUT" || Tag === "TEXTAREA" || Tag === "SELECT") { return true; }
                if (Cur instanceof HTMLElement && Cur.isContentEditable === true) { return true; }
                if (Cur.scrollWidth > Cur.clientWidth + 1) {
                    const Ox: string = window.getComputedStyle(Cur).overflowX;
                    if (Ox === "auto" || Ox === "scroll") { return true; }
                }
                Cur = Cur.parentElement;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.Ignored");
            return true;
        }
    };

    private OnStart = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Tracking = false;
            if (Ev.touches.length !== 1) { return; }
            if (kSwipeDetector.Ignored(Ev.target as Element | null, this.Target) === true) { return; }
            const X: number = Ev.touches[0].clientX;
            if (this.EdgeIgnorePx > 0 && (X < this.EdgeIgnorePx || X > window.innerWidth - this.EdgeIgnorePx)) { return; }
            this.StartX = Ev.touches[0].clientX;
            this.StartY = Ev.touches[0].clientY;
            this.Tracking = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.OnStart");
        }
    };

    private OnEnd = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Tracking === false || Ev.changedTouches.length === 0) { return; }
            this.Tracking = false;
            const Dx: number = Ev.changedTouches[0].clientX - this.StartX;
            const Dy: number = Ev.changedTouches[0].clientY - this.StartY;
            if (Math.abs(Dx) < this.ThresholdPx || Math.abs(Dx) < Math.abs(Dy) * this.MostlyRatio) { return; }
            let Direction: number = -1;
            if (Dx < 0) { Direction = 1; }
            this.OnSwipe(Direction);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.OnEnd");
        }
    };

    private OnCancel = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Tracking = false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSwipeDetector.OnCancel");
        }
    };
}

/**
 * Short haptic feedback on phones that support it (navigator.vibrate; iOS Safari and desktops have none and do nothing).
 * Tap = one short pulse for a confirmed touch action (a swipe that changed something, a message sent).
 */
export class kHaptics {
    // ERROR-SUPPRESSED-JUSTIFIED: one call; Vibrate carries the guard and the handler.
    public static Tap = (): void => { kHaptics.Vibrate(12); };

    public static Vibrate = (Pattern: number | number[]): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") { return; }
            navigator.vibrate(Pattern);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHaptics.Vibrate");
        }
    };
}

/**
 * The visible viewport's height (window.visualViewport: it shrinks when a phone's on-screen keyboard opens) kept in a CSS
 * variable on <html>, e.g. "--k-vh", so a full-height layout can use height:var(--k-vh,100dvh) and its bottom bar stays
 * above the keyboard. Without visualViewport the window's inner height is used.
 */
export class kViewportHeightVar {
    /** Optional: a class put on <html> while the on-screen keyboard is open ("" = no detection, the default). */
    public KeyboardClass: string = "";
    /** Optional: told on each change of the keyboard state (null = nobody). */
    public OnKeyboard: ((Open: boolean) => void) | null = null;
    private Open: boolean = false;
    private TallestAt: number = 0;
    private Tallest: number = 0;

    constructor(private readonly VarName: string = "--k-vh") { }

    /** DOCUMENTED EXCEPTION: a field read. Is the on-screen keyboard open (only with KeyboardClass / OnKeyboard)? */
    public KeyboardOpen = (): boolean => this.Open;

    public Attach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (window.visualViewport !== null && window.visualViewport !== undefined) {
                window.visualViewport.addEventListener("resize", this.Update);
                window.visualViewport.addEventListener("scroll", this.Update);
            }
            window.addEventListener("resize", this.Update);
            this.Update();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kViewportHeightVar.Attach");
        }
    };

    public Update = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let H: number = window.innerHeight;
            if (window.visualViewport !== null && window.visualViewport !== undefined) { H = window.visualViewport.height; }
            document.documentElement.style.setProperty(this.VarName, Math.round(H) + "px");
            this.NoteKeyboard(H);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kViewportHeightVar.Update");
        }
    };

    /** The keyboard is taken as open while the visible height is under 75% of the tallest seen at this width. */
    private NoteKeyboard = (H: number): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.KeyboardClass === "" && this.OnKeyboard === null) { return; }
            const W: number = window.innerWidth;
            if (W !== this.TallestAt) { this.TallestAt = W; this.Tallest = 0; }
            if (H > this.Tallest) { this.Tallest = H; }
            const Now: boolean = H < this.Tallest * 0.75;
            if (Now === this.Open) { return; }
            this.Open = Now;
            if (this.KeyboardClass !== "") {
                if (Now === true) { document.documentElement.classList.add(this.KeyboardClass); }
                if (Now === false) { document.documentElement.classList.remove(this.KeyboardClass); }
            }
            if (this.OnKeyboard !== null) { this.OnKeyboard(Now); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kViewportHeightVar.NoteKeyboard");
        }
    };
}

/**
 * Hides a top bar while the reader scrolls UP (back through older content) and shows it again on a scroll down, near the
 * end, or while a text field has focus: it sets HiddenClass on Root, the CSS decides what hides. Scroll events of every
 * scroller inside Root that matches ScrollerSelector count (captured, so nested scrollers work). Enabled() lets the
 * caller switch it off (e.g. on wide screens) - when false the class is removed.
 */
export class kAutoHideOnScroll {
    public MinStepPx: number = 8;
    /** Steady movement in one direction needed before the bar hides or shows (owner 2026-10-25: it flickered on a phone). */
    public HideAfterPx: number = 48;
    /** After a toggle, scroll events are ignored this long: hiding/showing the bar resizes the scroller, which scrolls. */
    public QuietMs: number = 300;
    /** Within this distance of the scroller's end the bar is always shown. */
    public NearEndPx: number = 80;
    public Enabled: () => boolean = kAutoHideOnScroll.Always;
    private LastEl: Element | null = null;
    private LastTop: number = 0;
    private Accum: number = 0;
    private QuietUntil: number = 0;

    constructor(private readonly Root: HTMLElement, private readonly ScrollerSelector: string, private readonly HiddenClass: string = "kBarHidden") { }

    /** DOCUMENTED EXCEPTION: pure default for Enabled. */
    public static Always = (): boolean => { return true; };

    public Attach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Root.addEventListener("scroll", this.OnScroll, true);
            document.addEventListener("focusin", this.OnFocusIn);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.Attach");
        }
    };

    public Show = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Root.classList.remove(this.HiddenClass);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.Show");
        }
    };

    /** true while a text field (input, textarea, editable element) has focus. */
    public static FieldFocused = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            const A: Element | null = document.activeElement;
            if (A === null) { return false; }
            if (A.tagName === "TEXTAREA" || A.tagName === "INPUT") { return true; }
            return A instanceof HTMLElement && A.isContentEditable === true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.FieldFocused");
            return false;
        }
    };

    private OnScroll = (Ev: Event): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const El: Element | null = Ev.target as Element | null;
            if (El === null || typeof El.matches !== "function" || El.matches(this.ScrollerSelector) === false) { return; }
            const Top: number = El.scrollTop;
            if (El !== this.LastEl) { this.LastEl = El; this.LastTop = Top; this.Accum = 0; return; }
            const Step: number = Top - this.LastTop;
            this.LastTop = Top;
            const NearEnd: boolean = El.scrollHeight - Top - El.clientHeight < this.NearEndPx;
            const Quiet: boolean = Date.now() < this.QuietUntil;
            // Near the end the bar shows at once - in the quiet period too when the reader moves down; the bar's own
            // toggle does not move the list down, so it cannot flip the bar back.
            if (NearEnd === true && (Quiet === false || Step > 0)) { this.Set(false); return; }
            if (Quiet === true) { return; }   // the bar's own toggle moved the scroller: not the reader
            if (Math.abs(Step) < 1) { return; }
            if ((Step < 0) !== (this.Accum < 0)) { this.Accum = 0; }   // direction changed: start counting again
            this.Accum += Step;
            if (Math.abs(this.Accum) < Math.max(this.MinStepPx, this.HideAfterPx)) { return; }
            this.Set(this.Enabled() === true && this.Accum < 0 && kAutoHideOnScroll.FieldFocused() === false);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.OnScroll");
        }
    };

    /** Hides or shows the bar; a real change starts the quiet period and the count again. */
    private Set = (Hide: boolean): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Root.classList.contains(this.HiddenClass) === Hide) { return; }
            this.Root.classList.toggle(this.HiddenClass, Hide);
            this.QuietUntil = Date.now() + this.QuietMs;
            this.Accum = 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.Set");
        }
    };

    private OnFocusIn = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kAutoHideOnScroll.FieldFocused() === true) { this.Show(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAutoHideOnScroll.OnFocusIn");
        }
    };
}

/**
 * A double tap (or double click) on one element: two clicks within WithinMs call Handler once. Uses the click event, so
 * it works for touch and mouse alike; a single tap does nothing. Bind adds the listener; the returned instance can Detach.
 */
export class kDoubleTap {
    private LastAt: number = 0;

    constructor(private readonly Target: HTMLElement, private readonly Handler: (Ev: Event) => void, private readonly WithinMs: number = 300) {
    }

    /** Binds a double-tap handler to El and answers the detector (null when it could not be bound). */
    public static Bind = (El: HTMLElement, Handler: (Ev: Event) => void, WithinMs: number = 300): kDoubleTap | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            const Tap: kDoubleTap = new kDoubleTap(El, Handler, WithinMs);
            El.addEventListener("click", Tap.OnClick);
            return Tap;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDoubleTap.Bind");
            return null;
        }
    };

    public Detach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Target.removeEventListener("click", this.OnClick);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDoubleTap.Detach");
        }
    };

    private OnClick = (Ev: Event): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Now: number = Date.now();
            if (Now - this.LastAt <= this.WithinMs) {
                this.LastAt = 0;
                this.Handler(Ev);
                return;
            }
            this.LastAt = Now;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDoubleTap.OnClick");
        }
    };
}

/** A long press on Target: the element the finger was on (the press's event target). */
export type kLongPressAction = (On: Element) => void;

/**
 * Long press (touch held HoldMs without moving more than MoveTolPx): OnHeld gets the pressed element, a light haptic
 * tick is given (kHaptics), and the click and the browser's own long-press menu that would follow are swallowed. A second
 * finger, a move or a lift before HoldMs cancels it. First consumer: Project Chats' phone pills (owner 2026-10-31).
 */
export class kLongPress {
    public HoldMs: number = 500;
    public MoveTolPx: number = 10;
    private Timer: number = 0;
    private StartX: number = 0;
    private StartY: number = 0;
    private On: Element | null = null;
    private Fired: boolean = false;

    constructor(private readonly Target: HTMLElement, private readonly OnHeld: kLongPressAction, HoldMs: number = 500) {
        this.HoldMs = HoldMs;
    }

    public Attach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Target.addEventListener("touchstart", this.OnStart, { passive: true });
            this.Target.addEventListener("touchmove", this.OnMove, { passive: true });
            this.Target.addEventListener("touchend", this.Stop, { passive: true });
            this.Target.addEventListener("touchcancel", this.Stop, { passive: true });
            this.Target.addEventListener("click", this.OnClick, true);
            this.Target.addEventListener("contextmenu", this.OnContextMenu);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.Attach");
        }
    };

    public Detach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Stop();
            this.Target.removeEventListener("touchstart", this.OnStart);
            this.Target.removeEventListener("touchmove", this.OnMove);
            this.Target.removeEventListener("touchend", this.Stop);
            this.Target.removeEventListener("touchcancel", this.Stop);
            this.Target.removeEventListener("click", this.OnClick, true);
            this.Target.removeEventListener("contextmenu", this.OnContextMenu);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.Detach");
        }
    };

    private OnStart = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Stop();
            this.Fired = false;
            if (Ev.touches.length !== 1) { return; }
            this.StartX = Ev.touches[0].clientX;
            this.StartY = Ev.touches[0].clientY;
            this.On = Ev.target as Element | null;
            this.Timer = window.setTimeout(this.OnTimer, this.HoldMs);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.OnStart");
        }
    };

    private OnMove = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Timer === 0 || Ev.touches.length === 0) { return; }
            const Dx: number = Ev.touches[0].clientX - this.StartX;
            const Dy: number = Ev.touches[0].clientY - this.StartY;
            if (Ev.touches.length > 1 || Math.abs(Dx) > this.MoveTolPx || Math.abs(Dy) > this.MoveTolPx) { this.Stop(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.OnMove");
        }
    };

    private OnTimer = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Timer = 0;
            if (this.On === null) { return; }
            this.Fired = true;
            kHaptics.Tap();
            this.OnHeld(this.On);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.OnTimer");
        }
    };

    private Stop = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Timer !== 0) { window.clearTimeout(this.Timer); }
            this.Timer = 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.Stop");
        }
    };

    // The click that ends a long press is not a tap. Only the finger's click (trusted): a click OnHeld makes itself (a
    // button's .click()) goes through.
    private OnClick = (Ev: MouseEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Fired === false || Ev.isTrusted === false) { return; }
            this.Fired = false;
            Ev.preventDefault();
            Ev.stopPropagation();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.OnClick");
        }
    };

    // The phone's own long-press menu (text selection, link menu) does not open over ours.
    private OnContextMenu = (Ev: MouseEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Fired === true || this.Timer !== 0) { Ev.preventDefault(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kLongPress.OnContextMenu");
        }
    };
}

/**
 * Pull to refresh on a scroll container: a downward drag that starts with the container at its top. While pulling, the
 * container carries the class kPulling and the CSS variable --k-pull (the pulled distance in px, damped, at most
 * 1.5 x ThresholdPx); past ThresholdPx also kPullReady. Lifting the finger when ready calls OnRefresh (with a haptic
 * tick). The consumer draws the indicator from those. First consumer: Project Chats' phone start pages (owner 2026-10-31).
 */
export class kPullToRefresh {
    public ThresholdPx: number = 70;
    private StartY: number = 0;
    private Tracking: boolean = false;
    private Ready: boolean = false;

    constructor(private readonly Scroller: HTMLElement, private readonly OnRefresh: () => void, ThresholdPx: number = 70) {
        this.ThresholdPx = ThresholdPx;
    }

    public Attach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Scroller.addEventListener("touchstart", this.OnStart, { passive: true });
            this.Scroller.addEventListener("touchmove", this.OnMove, { passive: true });
            this.Scroller.addEventListener("touchend", this.OnEnd, { passive: true });
            this.Scroller.addEventListener("touchcancel", this.Reset, { passive: true });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.Attach");
        }
    };

    public Detach = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Reset();
            this.Scroller.removeEventListener("touchstart", this.OnStart);
            this.Scroller.removeEventListener("touchmove", this.OnMove);
            this.Scroller.removeEventListener("touchend", this.OnEnd);
            this.Scroller.removeEventListener("touchcancel", this.Reset);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.Detach");
        }
    };

    private OnStart = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Tracking = Ev.touches.length === 1 && this.Scroller.scrollTop <= 0;
            this.Ready = false;
            if (this.Tracking === true) { this.StartY = Ev.touches[0].clientY; }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.OnStart");
        }
    };

    private OnMove = (Ev: TouchEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Tracking === false || Ev.touches.length === 0) { return; }
            const Dy: number = Ev.touches[0].clientY - this.StartY;
            if (Dy <= 0) { this.Show(0); return; }
            this.Show(Math.min(Dy * 0.5, this.ThresholdPx * 1.5));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.OnMove");
        }
    };

    private OnEnd = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Go: boolean = this.Tracking === true && this.Ready === true;
            this.Reset();
            if (Go === false) { return; }
            kHaptics.Tap();
            this.OnRefresh();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.OnEnd");
        }
    };

    private Reset = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Tracking = false;
            this.Show(0);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.Reset");
        }
    };

    // The pulled distance on the container (class + variable); 0 clears it.
    private Show = (Px: number): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Ready = Px >= this.ThresholdPx;
            this.Scroller.classList.toggle("kPulling", Px > 0);
            this.Scroller.classList.toggle("kPullReady", this.Ready);
            this.Scroller.style.setProperty("--k-pull", String(Math.round(Px)) + "px");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPullToRefresh.Show");
        }
    };
}
